import { REGEX_PATTERNS } from './constants';

/**
 * Validation utility functions
 */

// Basic validation helpers
export const isEmpty = (value) => {
  return value === null || value === undefined || value === '' ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' && Object.keys(value).length === 0);
};

export const isString = (value) => {
  return typeof value === 'string';
};

export const isNumber = (value) => {
  return typeof value === 'number' && !isNaN(value);
};

export const isInteger = (value) => {
  return Number.isInteger(value);
};

export const isBoolean = (value) => {
  return typeof value === 'boolean';
};

export const isArray = (value) => {
  return Array.isArray(value);
};

export const isObject = (value) => {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
};

// String validators
export const validateRequired = (value, message = 'This field is required') => {
  return !isEmpty(value) ? null : message;
};

export const validateMinLength = (value, minLength, message) => {
  if (isEmpty(value)) return null;

  const actualMessage = message || `Must be at least ${minLength} characters long`;
  return value.length >= minLength ? null : actualMessage;
};

export const validateMaxLength = (value, maxLength, message) => {
  if (isEmpty(value)) return null;

  const actualMessage = message || `Must be no more than ${maxLength} characters long`;
  return value.length <= maxLength ? null : actualMessage;
};

export const validateLength = (value, minLength, maxLength, message) => {
  if (isEmpty(value)) return null;

  const actualMessage = message || `Must be between ${minLength} and ${maxLength} characters long`;
  return value.length >= minLength && value.length <= maxLength ? null : actualMessage;
};

// Email validator
export const validateEmail = (email, message = 'Please enter a valid email address') => {
  if (isEmpty(email)) return null;

  return REGEX_PATTERNS.EMAIL.test(email) ? null : message;
};

// Phone validator
export const validatePhone = (phone, message = 'Please enter a valid phone number') => {
  if (isEmpty(phone)) return null;

  // Remove all non-digit characters for validation
  const cleanPhone = phone.replace(/\D/g, '');

  // Indian mobile number (10 digits) or with country code (12 digits starting with 91)
  if (cleanPhone.length === 10 || (cleanPhone.length === 12 && cleanPhone.startsWith('91'))) {
    return null;
  }

  // International format validation
  if (REGEX_PATTERNS.PHONE.test(phone)) {
    return null;
  }

  return message;
};

// URL validator
export const validateUrl = (url, message = 'Please enter a valid URL') => {
  if (isEmpty(url)) return null;

  try {
    new URL(url);
    return null;
  } catch {
    return message;
  }
};

// Number validators
export const validateNumber = (value, message = 'Please enter a valid number') => {
  if (isEmpty(value)) return null;

  return isNumber(Number(value)) ? null : message;
};

export const validateInteger = (value, message = 'Please enter a valid whole number') => {
  if (isEmpty(value)) return null;

  const num = Number(value);
  return Number.isInteger(num) ? null : message;
};

export const validateMin = (value, min, message) => {
  if (isEmpty(value)) return null;

  const num = Number(value);
  const actualMessage = message || `Must be at least ${min}`;

  return num >= min ? null : actualMessage;
};

export const validateMax = (value, max, message) => {
  if (isEmpty(value)) return null;

  const num = Number(value);
  const actualMessage = message || `Must be no more than ${max}`;

  return num <= max ? null : actualMessage;
};

export const validateRange = (value, min, max, message) => {
  if (isEmpty(value)) return null;

  const num = Number(value);
  const actualMessage = message || `Must be between ${min} and ${max}`;

  return num >= min && num <= max ? null : actualMessage;
};

// Date validators
export const validateDate = (value, message = 'Please enter a valid date') => {
  if (isEmpty(value)) return null;

  const date = new Date(value);
  return !isNaN(date.getTime()) ? null : message;
};

export const validateFutureDate = (value, message = 'Date must be in the future') => {
  if (isEmpty(value)) return null;

  const date = new Date(value);
  const now = new Date();

  return date > now ? null : message;
};

export const validatePastDate = (value, message = 'Date must be in the past') => {
  if (isEmpty(value)) return null;

  const date = new Date(value);
  const now = new Date();

  return date < now ? null : message;
};

export const validateDateRange = (startDate, endDate, message = 'End date must be after start date') => {
  if (isEmpty(startDate) || isEmpty(endDate)) return null;

  const start = new Date(startDate);
  const end = new Date(endDate);

  return end >= start ? null : message;
};

// Password validators
export const validatePassword = (password, minLength = 8, message) => {
  if (isEmpty(password)) return null;

  const actualMessage = message || `Password must be at least ${minLength} characters long`;

  if (password.length < minLength) {
    return actualMessage;
  }

  return null;
};

export const validateStrongPassword = (password, message = 'Password must contain at least 8 characters, including uppercase, lowercase, number, and special character') => {
  if (isEmpty(password)) return null;

  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
  const hasMinLength = password.length >= 8;

  if (hasUppercase && hasLowercase && hasNumber && hasSpecialChar && hasMinLength) {
    return null;
  }

  return message;
};

export const validatePasswordConfirmation = (password, confirmation, message = 'Passwords do not match') => {
  if (isEmpty(password) || isEmpty(confirmation)) return null;

  return password === confirmation ? null : message;
};

// Indian-specific validators
export const validatePincode = (pincode, message = 'Please enter a valid 6-digit pincode') => {
  if (isEmpty(pincode)) return null;

  return REGEX_PATTERNS.PINCODE.test(pincode) ? null : message;
};

export const validateGSTIN = (gstin, message = 'Please enter a valid GSTIN') => {
  if (isEmpty(gstin)) return null;

  // GSTIN format: 2 digits state code + 10 alphanumeric PAN + 1 digit entity code + 1 digit Z + 1 alphanumeric checksum
  const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;

  return gstinRegex.test(gstin.toUpperCase()) ? null : message;
};

export const validatePAN = (pan, message = 'Please enter a valid PAN number') => {
  if (isEmpty(pan)) return null;

  // PAN format: 5 letters + 4 digits + 1 letter
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

  return panRegex.test(pan.toUpperCase()) ? null : message;
};

export const validateAadhaar = (aadhaar, message = 'Please enter a valid Aadhaar number') => {
  if (isEmpty(aadhaar)) return null;

  // Remove spaces and check if it's 12 digits
  const cleanAadhaar = aadhaar.replace(/\s/g, '');
  const aadhaarRegex = /^[2-9]{1}[0-9]{11}$/;

  if (!aadhaarRegex.test(cleanAadhaar)) {
    return message;
  }

  // Verhoeff algorithm check for Aadhaar
  const verhoeffCheck = (num) => {
    const d = [
      [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
      [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
      [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
      [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
      [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
      [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
      [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
      [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
      [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
      [9, 8, 7, 6, 5, 4, 3, 2, 1, 0]
    ];

    const p = [
      [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
      [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
      [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
      [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
      [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
      [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
      [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
      [7, 0, 4, 6, 9, 1, 3, 2, 5, 8]
    ];

    let c = 0;
    const myArray = num.split('').reverse();

    for (let i = 0; i < myArray.length; i++) {
      c = d[c][p[((i + 1) % 8)][parseInt(myArray[i], 10)]];
    }

    return c === 0;
  };

  return verhoeffCheck(cleanAadhaar) ? null : message;
};

// Vehicle number validator
export const validateVehicleNumber = (vehicleNumber, message = 'Please enter a valid vehicle number') => {
  if (isEmpty(vehicleNumber)) return null;

  return REGEX_PATTERNS.VEHICLE_NUMBER.test(vehicleNumber.toUpperCase()) ? null : message;
};

// File validators
export const validateFileSize = (file, maxSizeInMB, message) => {
  if (!file) return null;

  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
  const actualMessage = message || `File size must be less than ${maxSizeInMB}MB`;

  return file.size <= maxSizeInBytes ? null : actualMessage;
};

export const validateFileType = (file, allowedTypes, message) => {
  if (!file) return null;

  const actualMessage = message || `File type must be one of: ${allowedTypes.join(', ')}`;
  const fileExtension = file.name.split('.').pop()?.toLowerCase();

  return allowedTypes.includes(fileExtension) || allowedTypes.includes(file.type) ? null : actualMessage;
};

// Array validators
export const validateArrayMinLength = (array, minLength, message) => {
  if (!Array.isArray(array)) return null;

  const actualMessage = message || `Must select at least ${minLength} item${minLength > 1 ? 's' : ''}`;
  return array.length >= minLength ? null : actualMessage;
};

export const validateArrayMaxLength = (array, maxLength, message) => {
  if (!Array.isArray(array)) return null;

  const actualMessage = message || `Cannot select more than ${maxLength} item${maxLength > 1 ? 's' : ''}`;
  return array.length <= maxLength ? null : actualMessage;
};

// Custom pattern validator
export const validatePattern = (value, pattern, message = 'Invalid format') => {
  if (isEmpty(value)) return null;

  const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
  return regex.test(value) ? null : message;
};

// Business logic validators
export const validateUniqueInArray = (value, array, key, message = 'This value already exists') => {
  if (isEmpty(value) || !Array.isArray(array)) return null;

  const duplicateExists = array.some(item => {
    const itemValue = key ? item[key] : item;
    return itemValue === value;
  });

  return !duplicateExists ? null : message;
};

// Composite validator
export const validateField = (value, validators) => {
  for (const validator of validators) {
    const error = validator(value);
    if (error) return error;
  }
  return null;
};

// Form validation helper
export const validateForm = (data, rules) => {
  const errors = {};
  let isValid = true;

  Object.keys(rules).forEach(field => {
    const fieldRules = Array.isArray(rules[field]) ? rules[field] : [rules[field]];
    const error = validateField(data[field], fieldRules);

    if (error) {
      errors[field] = error;
      isValid = false;
    }
  });

  return { isValid, errors };
};

export default {
  // Basic helpers
  isEmpty,
  isString,
  isNumber,
  isInteger,
  isBoolean,
  isArray,
  isObject,

  // String validators
  validateRequired,
  validateMinLength,
  validateMaxLength,
  validateLength,
  validateEmail,
  validatePhone,
  validateUrl,

  // Number validators
  validateNumber,
  validateInteger,
  validateMin,
  validateMax,
  validateRange,

  // Date validators
  validateDate,
  validateFutureDate,
  validatePastDate,
  validateDateRange,

  // Password validators
  validatePassword,
  validateStrongPassword,
  validatePasswordConfirmation,

  // Indian-specific validators
  validatePincode,
  validateGSTIN,
  validatePAN,
  validateAadhaar,
  validateVehicleNumber,

  // File validators
  validateFileSize,
  validateFileType,

  // Array validators
  validateArrayMinLength,
  validateArrayMaxLength,

  // Utility validators
  validatePattern,
  validateUniqueInArray,
  validateField,
  validateForm
};